import React from 'react';
import { Link } from 'react-router-dom';
import {
  controlReasonsOf,
  licencePostureOf,
  primaryControlOf,
  reasonPhrase,
} from 'components/ExportDesign/exportDesignControl';
import { humanizeKey, humanizeSpecRef } from 'components/ExportDesign/exportDesignProgress';

// --- Rich renderer for the Export Design Advisor's consolidated report. Consumes the structured
// `report` frame payload the backend emits on a report turn (spec_sheet / classification / ladder /
// licensing + the synthesized sections), so the deliverable reads as a decision-ready card instead
// of a wall of markdown. All colours come from the shared CSS tokens so both themes work.

interface ReportOption {
  label?: string;
  target_regime?: string;
  change_summary?: string;
  feasibility?: string | null;
  tradeoff?: string | null;
  recommended?: boolean;
}
interface ReportSynthesis {
  executive_summary?: string;
  options?: ReportOption[];
  options_note?: string | null;
  licensing_summary?: string | null;
  overall_confidence?: string | null;
  recommended_next_step?: string;
  caveats?: string[];
}
interface BindingConstraint {
  citation?: string;
  control_text?: string;
  reason_for_control?: string | null;
  matched_spec?: string | null;
  explanation?: string | null;
  decontrol_threshold?: string | null;
}
interface Classification {
  current_regime?: string;
  candidate_eccns?: string[];
  candidate_usml?: string[];
  binding_constraints?: BindingConstraint[];
}
interface LadderRung {
  regime?: string;
  citation?: string | null;
  is_current?: boolean;
  reachable?: boolean;
  change_summary?: string | null;
  consequence?: string;
  licensing_note?: string | null;
}
interface Licensing {
  destination_country?: string;
  regime?: string;
  license_required?: boolean | null;
  basis?: string[];
  embargoed?: boolean;
  itar_proscribed?: boolean | null;
}
export interface DesignReportPayload {
  spec_sheet?: Record<string, unknown>;
  product?: { name?: string | null; type?: string | null; description?: string | null };
  classification?: Classification | null;
  ladder?: { rungs?: LadderRung[] } | null;
  licensing?: Licensing | null;
  synthesis?: ReportSynthesis;
}

const REGIME_META: Record<string, { label: string; cls: string }> = {
  ITAR: { label: 'ITAR', cls: 'regime-itar' },
  'EAR-listed': { label: 'EAR-listed', cls: 'regime-ear-listed' },
  EAR99: { label: 'EAR99', cls: 'regime-ear99' },
  uncertain: { label: 'Uncertain', cls: 'regime-uncertain' },
};

const licenseVerdict = (v?: boolean | null): string =>
  v === true
    ? 'License required'
    : v === false
    ? 'No license required (on the inputs checked)'
    : 'License requirement uncertain';

const ExportDesignReportCard: React.FC<{
  report: DesignReportPayload;
  onSendToExportWorkflow?: (productDescription: string) => void;
  onSaveResult?: () => void;
  onShareResult?: () => void;
  // Same conditions as the page header's buttons (no Business access, nothing to save yet, a save
  // already in flight): the buttons stay visible but inert, so a double click can't start two saves.
  disableSave?: boolean;
  disableShare?: boolean;
  // Set once the result is saved and unchanged: the Save button becomes a link to the ticket.
  savedTicketPath?: string;
}> = ({
  report,
  onSendToExportWorkflow,
  onSaveResult,
  onShareResult,
  disableSave,
  disableShare,
  savedTicketPath,
}) => {
  const synthesis: ReportSynthesis = report.synthesis || {};
  const classification: Classification = report.classification || {};
  const specEntries = Object.entries(report.spec_sheet || {});
  const rungs: LadderRung[] = report.ladder?.rungs || [];
  const licensing = report.licensing || undefined;
  const options = synthesis.options || [];
  const caveats = (synthesis.caveats || []).filter((c) => c && c.trim());

  const regime = classification.current_regime || 'uncertain';
  const regimeMeta = REGIME_META[regime] || REGIME_META.uncertain;
  // What the item is controlled under and for LEADS; "EAR-listed" only says it has an ECCN.
  const primaryControl = primaryControlOf(classification);
  const controlReasons = controlReasonsOf(classification);
  const licencePosture = licencePostureOf(classification);
  const otherControls = (classification.candidate_eccns || classification.candidate_usml || []).filter(
    (c) => c && c !== primaryControl,
  );

  const product = report.product || {};
  const rawSubtitle = product.name || product.type || product.description || '';
  const subtitle =
    rawSubtitle.length > 120 ? `${rawSubtitle.slice(0, 117).trimEnd()}…` : rawSubtitle;

  // The richest single product description to seed the forward Export Workflow with: the
  // description fact if present, else the product type/name + the spec sheet inlined.
  const buildSeedDescription = (): string => {
    if (product.description && product.description.trim()) return product.description.trim();
    const parts: string[] = [];
    if (product.type) parts.push(product.type);
    else if (product.name) parts.push(product.name);
    if (specEntries.length > 0) {
      parts.push(
        `Specifications: ${specEntries
          .map(([k, v]) => `${humanizeKey(k)} ${String(v)}`)
          .join(', ')}`,
      );
    }
    return parts.join('. ');
  };

  return (
    <div className='export-design-report'>
      <div className='export-design-report-head'>
        <div>
          <div className='export-design-report-title'>Final Design Summary</div>
          {subtitle && <div className='export-design-report-subtitle'>{subtitle}</div>}
        </div>
        <span className={`export-design-regime-badge ${regimeMeta.cls}`}>
          {primaryControl ? `${primaryControl} · ${regimeMeta.label}` : regimeMeta.label}
        </span>
      </div>

      {synthesis.executive_summary && (
        <p className='export-design-report-summary'>{synthesis.executive_summary.trim()}</p>
      )}

      {specEntries.length > 0 && (
        <section className='export-design-report-section'>
          <h4 className='export-design-report-h'>Product specification</h4>
          <table className='export-design-report-spec'>
            <tbody>
              {specEntries.map(([k, v]) => (
                <tr key={k}>
                  <td className='export-design-report-spec-key'>{humanizeKey(k)}</td>
                  <td className='export-design-report-spec-val'>{String(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className='export-design-report-section'>
        <h4 className='export-design-report-h'>Current classification</h4>
        <div className='export-design-report-regime-line'>
          {primaryControl && <span className='export-design-report-code'>{primaryControl}</span>}
          {controlReasons.length > 0 && (
            <span className='export-design-report-controlled-for'>
              controlled for {reasonPhrase(controlReasons)}
            </span>
          )}
          <span className={`export-design-regime-badge ${regimeMeta.cls}`}>{regimeMeta.label}</span>
        </div>
        {licencePosture && <p className='export-design-report-posture'>{licencePosture}</p>}
        {otherControls.length > 0 && (
          <div className='export-design-report-muted'>Also considered: {otherControls.join(', ')}</div>
        )}
        {(classification.binding_constraints || []).length > 0 && (
          <table className='export-design-report-bc-table'>
            <thead>
              <tr>
                <th>Control</th>
                <th>Your spec</th>
                <th>What would decontrol it</th>
              </tr>
            </thead>
            <tbody>
              {(classification.binding_constraints || []).map((bc, i) => (
                <tr key={`${bc.citation}-row-${i}`}>
                  <td>
                    <span className='export-design-report-code'>{bc.citation}</span>
                    {bc.reason_for_control && (
                      <span className='export-design-report-muted'> ({bc.reason_for_control})</span>
                    )}
                  </td>
                  <td>{bc.matched_spec ? humanizeSpecRef(bc.matched_spec) : '—'}</td>
                  <td>{bc.decontrol_threshold || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {(classification.binding_constraints || []).map((bc, i) => (
          <div className='export-design-report-bc' key={`${bc.citation}-${i}`}>
            <div className='export-design-report-bc-head'>
              <span className='export-design-report-code'>{bc.citation}</span>
              {bc.reason_for_control && (
                <span className='export-design-report-muted'>
                  {' '}
                  controlled for {bc.reason_for_control}
                </span>
              )}
            </div>
            {bc.control_text && (
              <div className='export-design-report-quote'>“{bc.control_text.trim()}”</div>
            )}
            {bc.matched_spec && (
              <div className='export-design-report-muted'>Your product: {humanizeSpecRef(bc.matched_spec)}</div>
            )}
          </div>
        ))}
      </section>

      {options.length > 0 && (
      <section className='export-design-report-section'>
        <h4 className='export-design-report-h'>Design options to change the outcome</h4>
          {options
            .slice()
            .sort((a, b) => Number(!!b.recommended) - Number(!!a.recommended))
            .map((o, i) => (
              <div
                className={`export-design-option${o.recommended ? ' is-recommended' : ''}`}
                key={`${o.label}-${i}`}
              >
                <div className='export-design-option-head'>
                  <span className='export-design-option-label'>{o.label}</span>
                  {o.target_regime && (
                    <span className='export-design-option-target'>→ {o.target_regime}</span>
                  )}
                  {o.recommended && (
                    <span className='export-design-option-rec'>★ recommended</span>
                  )}
                </div>
                {o.change_summary && (
                  <div className='export-design-option-line'>
                    <strong>Change:</strong> {o.change_summary.trim()}
                  </div>
                )}
                {o.feasibility && (
                  <div className='export-design-option-line'>
                    <strong>Feasibility:</strong> {o.feasibility.trim()}
                  </div>
                )}
                {o.tradeoff && (
                  <div className='export-design-option-line'>
                    <strong>Trade-off:</strong> {o.tradeoff.trim()}
                  </div>
                )}
              </div>
            ))}
      </section>
      )}

      {rungs.length > 0 && (
        <section className='export-design-report-section'>
          <h4 className='export-design-report-h'>Regime ladder</h4>
          <ul className='export-design-ladder'>
            {rungs.map((rung, i) => {
              const state = rung.is_current
                ? 'is-current'
                : rung.reachable === false
                ? 'is-blocked'
                : 'is-reachable';
              return (
                <li className={`export-design-ladder-rung ${state}`} key={`${rung.regime}-${i}`}>
                  <span className='export-design-ladder-marker' aria-hidden='true' />
                  <div className='export-design-ladder-body'>
                    <div className='export-design-ladder-regime'>
                      {rung.regime}
                      {rung.citation && (
                        <span className='export-design-report-code'> {rung.citation}</span>
                      )}
                      {rung.is_current && (
                        <span className='export-design-ladder-tag'>current</span>
                      )}
                      {!rung.is_current && rung.reachable === false && (
                        <span className='export-design-ladder-tag'>not reachable by design</span>
                      )}
                    </div>
                    {rung.consequence && (
                      <div className='export-design-report-muted'>{rung.consequence.trim()}</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {(licensing || synthesis.licensing_summary) && (
        <section className='export-design-report-section'>
          <h4 className='export-design-report-h'>Licensing</h4>
          {licensing && (
            <div className='export-design-report-lic'>
              <strong>{licensing.destination_country || 'Destination'}:</strong>{' '}
              {licenseVerdict(licensing.license_required)}
              {licensing.itar_proscribed ? ' — ITAR §126.1 proscribed (generally denied)' : ''}
              {licensing.embargoed ? ' — comprehensively embargoed' : ''}
              {(licensing.basis || []).length > 0 && (
                <ul className='export-design-report-caveats'>
                  {(licensing.basis || []).map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {synthesis.licensing_summary && (
            <p className='export-design-report-summary'>{synthesis.licensing_summary.trim()}</p>
          )}
        </section>
      )}

      {synthesis.recommended_next_step && (
        <section className='export-design-report-section'>
          <h4 className='export-design-report-h'>Recommended next step</h4>
          <p className='export-design-report-summary'>{synthesis.recommended_next_step.trim()}</p>
        </section>
      )}

      {synthesis.overall_confidence && (
        <div className='export-design-report-conf'>
          Overall confidence: <strong>{synthesis.overall_confidence}</strong>
        </div>
      )}

      {caveats.length > 0 && (
        <section className='export-design-report-section'>
          <h4 className='export-design-report-h'>Caveats</h4>
          <ul className='export-design-report-caveats'>
            {caveats.map((c, i) => (
              <li key={i}>{c.trim()}</li>
            ))}
          </ul>
        </section>
      )}

      {(onSendToExportWorkflow || onSaveResult || onShareResult || savedTicketPath) && (
        <div className='export-design-report-actions'>
          {(onSaveResult || onShareResult || savedTicketPath) && (
            <div className='export-design-report-btn-row'>
              {savedTicketPath ? (
                <Link className='standard-workflow-secondary-btn' to={savedTicketPath}>
                  View saved result
                </Link>
              ) : (
                onSaveResult && (
                  <button
                    type='button'
                    className='standard-workflow-secondary-btn'
                    disabled={disableSave}
                    onClick={onSaveResult}
                  >
                    Save
                  </button>
                )
              )}
              {onShareResult && (
                <button
                  type='button'
                  className='standard-workflow-secondary-btn'
                  disabled={disableShare}
                  onClick={onShareResult}
                >
                  Share
                </button>
              )}
            </div>
          )}
          {/* The forward-workflow action is a different kind of next step: a panel of its own, with
              what it does stated beside the button (the note used to trail Share and read as
              Share's hint). */}
          {onSendToExportWorkflow && (
            <div className='export-design-report-forward'>
              <div className='export-design-report-forward-text'>
                <strong>Continue in the Export Workflow</strong>
                <span className='export-design-report-actions-note'>
                  Opens the formal classification and licensing workflow with this product
                  pre-filled for review.
                </span>
              </div>
              <button
                type='button'
                className='export-design-report-btn'
                onClick={() => onSendToExportWorkflow(buildSeedDescription())}
              >
                Send to Export Workflow →
              </button>
            </div>
          )}
        </div>
      )}

      <p className='export-design-report-disclaimer'>
        This is a research tool, not a formal ruling — please consult counsel for a final
        determination (or request a CJ from State/DDTC, or a CCATS classification from BIS).
      </p>
    </div>
  );
};

export default ExportDesignReportCard;
